import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  CreditCard, 
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  Receipt,
  Wallet,
  AlertCircle,
  TrendingUp,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CardLinkingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [linkedCards, setLinkedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  
  const [cardForm, setCardForm] = useState({
    card_type: "credit",
    last_four: "",
    bank_name: "",
    card_network: "visa"
  });

  const banks = ["Chase", "Bank of America", "Wells Fargo", "Citi", "Capital One", "American Express", "Discover", "US Bank", "PNC", "Other"];
  const cardNetworks = ["visa", "mastercard", "amex", "discover"];

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchLinkedCards(userData.id);
  }, [navigate]);

  const fetchLinkedCards = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cards/${userId}`);
      setLinkedCards(response.data.cards || []);
    } catch (error) {
      console.error("Error fetching cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkCard = async () => {
    if (!cardForm.last_four || cardForm.last_four.length !== 4 || !cardForm.bank_name) {
      toast.error("Please fill in all fields correctly");
      return;
    }

    setLinking(true);
    setLinkResult(null);
    
    try {
      const response = await axios.post(`${API}/cards/link`, {
        user_id: user.id,
        ...cardForm
      });
      
      setLinkResult(response.data);
      toast.success(`Card linked! Imported ${response.data.transactions_imported} transactions`);
      fetchLinkedCards(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link card");
      setLinkResult(null);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkCard = async (cardId) => {
    try {
      await axios.delete(`${API}/cards/${cardId}?user_id=${user.id}`);
      toast.success("Card unlinked");
      fetchLinkedCards(user.id);
    } catch (error) {
      toast.error("Failed to unlink card");
    }
  };

  const closeLinkDialog = () => {
    setLinkDialogOpen(false);
    setLinkResult(null);
    setCardForm({
      card_type: "credit",
      last_four: "",
      bank_name: "",
      card_network: "visa"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-teal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-semibold">Link Cards</h1>
            <p className="text-xs text-muted-foreground">Connect your cards to track spending</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-tamba-teal/10 to-tamba-blue/10 border border-tamba-teal/30 rounded-xl p-4"
        >
          <div className="flex gap-3">
            <Sparkles className="h-5 w-5 text-tamba-teal flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">AI-Powered Budget Insights</p>
              <p className="text-sm text-muted-foreground">
                Link your cards to automatically import 30-90 days of transactions. Our AI analyzes your spending to create a personalized budget.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Linked Cards */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Your Cards</h2>
            <Button
              onClick={() => setLinkDialogOpen(true)}
              className="rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
              data-testid="link-card-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Link Card
            </Button>
          </div>

          {linkedCards.length === 0 ? (
            <Card className="border-dashed border-2 border-border/50">
              <CardContent className="py-12 text-center">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-semibold mb-2">No Cards Linked</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Link a credit or debit card to start tracking your expenses automatically
                </p>
                <Button 
                  onClick={() => setLinkDialogOpen(true)}
                  className="rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Link Your First Card
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {linkedCards.map((card, index) => (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="border-border/50 hover:border-tamba-teal/30 transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          card.card_type === "credit" ? "bg-purple-100" : "bg-tamba-teal/10"
                        }`}>
                          <CreditCard className={`h-6 w-6 ${
                            card.card_type === "credit" ? "text-purple-600" : "text-tamba-teal"
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{card.bank_name}</h3>
                            <span className="text-xs px-2 py-0.5 bg-secondary rounded-full capitalize">
                              {card.card_type}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            •••• {card.last_four} · {card.card_network.toUpperCase()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleUnlinkCard(card.id)}
                          data-testid={`unlink-card-${card.last_four}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* What Happens Next */}
        {linkedCards.length > 0 && (
          <Card className="border-tamba-blue/30 bg-gradient-to-br from-tamba-blue/5 to-transparent">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-tamba-blue" />
                What's Next?
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Your transaction history is imported</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>AI analyzes your spending patterns</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-tamba-yellow mt-0.5 flex-shrink-0" />
                  <span>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-tamba-blue"
                      onClick={() => navigate("/budget")}
                    >
                      Go to Budget →
                    </Button>
                    {" "}to see your AI-generated budget
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Link Card Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={closeLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-tamba-teal" />
              Link a Card
            </DialogTitle>
            <DialogDescription>
              Connect your credit or debit card to import transactions
            </DialogDescription>
          </DialogHeader>

          {!linkResult ? (
            <div className="py-4 space-y-4">
              <div className="bg-tamba-yellow/10 border border-tamba-yellow/30 rounded-lg p-3 flex items-center gap-2 text-sm">
                <AlertCircle className="h-4 w-4 text-tamba-yellow flex-shrink-0" />
                <span>Demo Mode: Card details are simulated for this prototype</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Card Type</Label>
                  <Select 
                    value={cardForm.card_type} 
                    onValueChange={(v) => setCardForm(prev => ({ ...prev, card_type: v }))}
                  >
                    <SelectTrigger data-testid="card-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Credit Card</SelectItem>
                      <SelectItem value="debit">Debit Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Card Network</Label>
                  <Select 
                    value={cardForm.card_network} 
                    onValueChange={(v) => setCardForm(prev => ({ ...prev, card_network: v }))}
                  >
                    <SelectTrigger data-testid="card-network-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cardNetworks.map(network => (
                        <SelectItem key={network} value={network}>
                          {network.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Bank</Label>
                <Select 
                  value={cardForm.bank_name} 
                  onValueChange={(v) => setCardForm(prev => ({ ...prev, bank_name: v }))}
                >
                  <SelectTrigger data-testid="bank-select">
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map(bank => (
                      <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Last 4 Digits</Label>
                <Input
                  type="text"
                  maxLength={4}
                  placeholder="1234"
                  value={cardForm.last_four}
                  onChange={(e) => setCardForm(prev => ({ ...prev, last_four: e.target.value.replace(/\D/g, '') }))}
                  data-testid="last-four-input"
                />
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-6 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">Card Linked!</h3>
              <p className="text-muted-foreground mb-4">
                Successfully imported {linkResult.transactions_imported} transactions
                <br />
                from the past {linkResult.days_of_data} days
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Receipt className="h-4 w-4" />
                <span>Ready for AI budget analysis</span>
              </div>
            </motion.div>
          )}

          <DialogFooter>
            {!linkResult ? (
              <>
                <Button variant="outline" onClick={closeLinkDialog}>Cancel</Button>
                <Button
                  onClick={handleLinkCard}
                  disabled={linking || !cardForm.last_four || !cardForm.bank_name}
                  className="rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
                  data-testid="confirm-link-btn"
                >
                  {linking ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Linking...
                    </>
                  ) : (
                    "Link Card"
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  closeLinkDialog();
                  navigate("/budget");
                }}
                className="w-full rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
              >
                View AI Budget
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CardLinkingPage;
